import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { IncomingVariantStockDto } from '@/api-sdk';
import { iconCaret } from '@/components/icons';
import { global, GlobalComponentType } from '@/context';
import { BREAKPOINT_EXTRA_LARGE_PX, BREAKPOINT_MEDIUM_PX } from '@/settings';
import {
  baseStyles,
  quantityDetailsStyles,
  rootVariablesStyles,
} from '@/styles';
import { formatDate, getCountryCode } from '@/utils';

@customElement('fg-quantity-back-order')
@localized()
export class FGQuantityBackOrder extends LitElement {
  /**
   * Total incoming quantity to render the to render the total incoming quantity, This data is received from the quantity
   * call.
   */
  @property({ type: Number })
  totalIncoming?: number;

  /**
   * Incoming stock data to render the backorder summary using {@link IncomingVariantStockDto}. This data is received
   * from the quantity call to the configurator.
   */
  @property({ type: Array })
  incomingStocks?: IncomingVariantStockDto[];

  /**
   * Locale to render the date using the {@link formatDate} function.
   */
  @property({ type: String })
  locale = 'nl_NL';

  @state()
  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  render() {
    if (!this.totalIncoming || !this.incomingStocks) {
      return;
    }

    const { locale } = this.globalContext;

    return html`
      <details>
        <summary>
          <div>
            <span class="backorder-label">${t('labels.inBackOrder')}</span>
            <div class="quantity">
              <span>
                +${Intl.NumberFormat(getCountryCode(locale)).format(
                  this.totalIncoming,
                )}
                ${iconCaret}
              </span>
            </div>
          </div>
        </summary>
        <div class="backorder-details">
          ${this.incomingStocks?.map((incoming: IncomingVariantStockDto) => {
            return html`
              <ul class="backorder-dates">
                <li>
                  <span>
                    ${incoming.expectedArrivalDate !== null
                      ? formatDate(
                          incoming.expectedArrivalDate,
                          locale,
                          'numeric',
                        )
                      : t('labels.toBeConfirmed')}:
                  </span>
                  <span class="incoming-quantity">
                    +${Intl.NumberFormat(getCountryCode(locale)).format(
                      incoming.quantity,
                    )}
                  </span>
                </li>
              </ul>
            `;
          })}
        </div>
      </details>
    `;
  }

  static styles = [
    baseStyles,
    rootVariablesStyles,
    quantityDetailsStyles,
    css`
      details summary {
        display: flex;
        cursor: pointer;
        gap: 4px;
        align-items: center;
        font-size: 1rem;
      }

      details summary div {
        display: flex;
        justify-content: space-between;
        gap: 10px;
        width: 100%;
        flex-grow: 1;
      }

      .backorder-label {
        white-space: nowrap;
      }

      details summary::marker,
      details summary::-webkit-details-marker {
        display: none;
      }

      details summary svg {
        flex-shrink: 0;
        margin-bottom: 0.125rem;
      }

      details[open] summary svg {
        transform: rotate(180deg);
      }

      .quantity {
        color: var(--fg-secondary, var(--fg-fallback-secondary));
        font-weight: 600;
        text-decoration: underline;
        text-underline-offset: 2px;
        justify-content: end;
        white-space: nowrap;
        display: flex;
        align-items: center;
        gap: 0.25rem;
      }

      .backorder-details {
        padding-right: 15px;
      }

      .backorder-dates li {
        display: flex;
        width: 100%;
        justify-content: space-between;
        margin-right: 15px;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        details summary {
          font-size: 0.875rem;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
        .backorder-dates li {
          justify-content: space-between;
          margin-right: 0px;
        }

        .backorder-details {
          padding-right: 0px;
        }

        details summary div {
          justify-content: flex-end;
        }

        .backorder-label {
          justify-content: end;
          display: flex;
        }

        .backorder-dates {
          display: flex;
          justify-content: end;
        }

        .incoming-quantity {
          margin-right: 15px;
        }

        .incoming-quantity,
        .quantity {
          display: flex;
          justify-content: flex-end;
          width: 70px;
        }
      }
    `,
  ];
}
